import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clearSession,
  fetchMe,
  getStoredUser,
  getToken,
  isDiscordFrame,
  resetAuthInflight,
  setSession,
} from "./session";
import type { SessionUser } from "./session";
import { isApiError } from "./api";
import { getDiscordAuth, resetDiscordAuth } from "../discord/sdk";
import { navigate, parseRoute } from "./router";
import type { Route } from "./router";

export interface AuthState {
  token: string;
  user: SessionUser | null;
  discordMode: boolean;
  discordError: string | null;
  route: Route;
  authed: boolean;
  ready: boolean;
  retryDiscord: () => void;
  logout: () => void;
  handleAuthDone: () => void;
  goGame: (guildId: string) => void;
}

export function useAuth(): AuthState {
  const discordMode = useMemo(() => isDiscordFrame(), []);
  const [route, setRoute] = useState<Route>(() => parseRoute());
  const [session, setSessionState] = useState<{ token: string; user: SessionUser | null }>(() => {
    const t = getToken();
    return t ? { token: t, user: getStoredUser() } : { token: "", user: null };
  });
  const [discordError, setDiscordError] = useState<string | null>(null);
  const [ready, setReady] = useState<boolean>(() => discordMode);

  useEffect(() => {
    const onPop = () => setRoute(parseRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const discordInit = useRef(false);
  useEffect(() => {
    if (!discordMode) return;
    if (discordInit.current) return;
    discordInit.current = true;
    let cancelled = false;
    const boot = async () => {
      try {
        const id = await getDiscordAuth();
        if (cancelled) return;
        setSession(id.accessToken, id.user);
        setSessionState({ token: id.accessToken, user: id.user });
      } catch (e) {
        if (cancelled) return;
        setDiscordError(e instanceof Error ? e.message : "Discord auth failed.");
      } finally {
        if (!cancelled) setReady(true);
      }
    };
    void boot();
    return () => {
      cancelled = true;
    };
  }, [discordMode]);

  useEffect(() => {
    if (discordMode) return;
    const stored = getToken();
    if (!stored) {
      setReady(true);
      return;
    }
    let mounted = true;
    const load = async (): Promise<SessionUser> => {
      try {
        return await fetchMe(stored);
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return await fetchMe(stored);
        throw e;
      }
    };
    const boot = async () => {
      try {
        const user = await load();
        if (!mounted) return;
        setSession(stored, user);
        setSessionState({ token: stored, user });
      } catch (e) {
        if (!mounted) return;
        if (e instanceof DOMException && e.name === "AbortError") return;
        if (isApiError(e) && (e.status === 401 || e.status === 403)) {
          clearSession();
          setSessionState({ token: "", user: null });
        }
      } finally {
        if (mounted) setReady(true);
      }
    };
    void boot();
    return () => {
      mounted = false;
    };
  }, [discordMode]);

  const handleAuthDone = useCallback(() => {
    const t = getToken();
    setSessionState({ token: t ?? "", user: getStoredUser() });
    setReady(true);
    navigate("/");
  }, []);

  const logout = useCallback(() => {
    clearSession();
    resetDiscordAuth();
    resetAuthInflight();
    setDiscordError(null);
    setSessionState({ token: "", user: null });
    setReady(true);
    navigate("/");
  }, []);

  const retryDiscord = useCallback(() => {
    resetDiscordAuth();
    setDiscordError(null);
    window.location.reload();
  }, []);

  const goGame = useCallback((guildId: string) => {
    const clean = guildId.trim();
    if (!/^[0-9]{17,20}$/.test(clean)) return;
    navigate("/bakery/" + encodeURIComponent(clean));
  }, []);

  return {
    token: session.token,
    user: session.user,
    discordMode,
    discordError,
    route,
    authed: session.token !== "",
    ready,
    retryDiscord,
    logout,
    handleAuthDone,
    goGame,
  };
}
